import { useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";

export type WaferDie = {
  id: string;
  columnIndex: number;
  rowIndex: number;
  x: number;
  y: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  isReference?: boolean;
};

export type ChipPosSelectorProps = {
  /** The diameter of the wafer in mm */
  diameter?: number;
  /** The width of a die in µm */
  dieWidth?: number;
  /** The height of a die in µm */
  dieHeight?: number;
  /** Define x offset between wafer center and die center in µm. If not provided, defaults to half the die width */
  dieOffsetX?: number | null;
  /** Define y offset between wafer center and die center in µm. If not provided, defaults to half the die height */
  dieOffsetY?: number | null;
  /** Optional reference die center X (µm) relative to wafer center. If provided, used to select the reference die. */
  referenceDieX?: number | null;
  /** Optional reference die center Y (µm) relative to wafer center. If provided, used to select the reference die. */
  referenceDieY?: number | null;
  /** The edge indent of the die in mm */
  edgeIndent?: number;
  /** The street width between dies in µm */
  street?: number;
  /** The stroke width of the wafer circle in mm */
  waferStrokeWidth?: number;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Callback when a die is clicked */
  onDieClick?: (die: WaferDie) => void;
  /** Callback when dies are calculated/updated */
  onDiesCalculated?: (dies: WaferDie[]) => void;
  /** Ordered array of selected die IDs (order matters for display) */
  selectedDieIds?: string[];
  /** Whether the component is in "change reference die" mode */
  isChangingReferenceDie?: boolean;
  /** Optional className for the SVG container */
  className?: string;
};

const isRectangleInsideCircle = (
  cx: number,
  cy: number,
  halfWidth: number,
  halfHeight: number,
  limit: number,
): boolean => {
  const corners: Array<[number, number]> = [
    [-halfWidth, -halfHeight],
    [-halfWidth, halfHeight],
    [halfWidth, -halfHeight],
    [halfWidth, halfHeight],
  ];

  return corners.every(([dx, dy]) => Math.hypot(cx + dx, cy + dy) <= limit + Number.EPSILON);
};

export function ChipPosSelector({
  diameter = 300,
  dieWidth = 22310,
  dieHeight = 14870,
  dieOffsetX = null,
  dieOffsetY = null,
  referenceDieX = null,
  referenceDieY = null,
  edgeIndent = 5,
  street = 5,
  waferStrokeWidth = 2,
  ariaLabel = "Wafer layout",
  onDieClick,
  onDiesCalculated,
  selectedDieIds = [],
  className,
}: ChipPosSelectorProps) {
  // Convert and clamp values
  const clampedWaferDiameter = useMemo(() => Math.max(diameter * 1000, 0), [diameter]);
  const clampedDieWidth = useMemo(() => Math.max(dieWidth, 0), [dieWidth]);
  const clampedDieHeight = useMemo(() => Math.max(dieHeight, 0), [dieHeight]);
  const clampedDieOffsetX = useMemo(() => {
    if (dieOffsetX === null || dieOffsetX === undefined) {
      return clampedDieWidth / 2;
    }
    return Math.max(dieOffsetX, 0);
  }, [dieOffsetX, clampedDieWidth]);
  const clampedDieOffsetY = useMemo(() => {
    if (dieOffsetY === null || dieOffsetY === undefined) {
      return clampedDieHeight / 2;
    }
    return Math.max(dieOffsetY, 0);
  }, [dieOffsetY, clampedDieHeight]);
  const clampedEdgeIndent = useMemo(() => Math.max(edgeIndent * 1000, 0), [edgeIndent]);
  const clampedStreet = useMemo(() => Math.max(street, 0), [street]);
  const clampedWaferStrokeWidth = useMemo(
    () => Math.max(waferStrokeWidth * 1000, 0),
    [waferStrokeWidth],
  );

  const radius = useMemo(() => clampedWaferDiameter / 2, [clampedWaferDiameter]);
  const edgeIndentRadius = useMemo(
    () => Math.max(radius - clampedEdgeIndent, 0),
    [radius, clampedEdgeIndent],
  );
  const strokePadding = useMemo(() => clampedWaferStrokeWidth / 2, [clampedWaferStrokeWidth]);

  const viewBox = useMemo(() => {
    const diameter = clampedWaferDiameter;
    const padding = strokePadding;
    return `${-padding} ${-padding} ${diameter + padding * 2} ${diameter + padding * 2}`;
  }, [clampedWaferDiameter, strokePadding]);

  const dies = useMemo<WaferDie[]>(() => {
    const dieWidthVal = clampedDieWidth;
    const dieHeightVal = clampedDieHeight;
    const edgeIndentVal = clampedEdgeIndent;
    const xOffset = clampedDieOffsetX;
    const yOffset = clampedDieOffsetY;

    // active wafer (no dies closer than edgeIndent)
    const waferDiameter = Math.max(clampedWaferDiameter, 0);
    const activeWafer = Math.max(waferDiameter - 2 * edgeIndentVal, 0);

    // quick impossible checks
    if (dieWidthVal <= 0 || dieHeightVal <= 0) {
      return [];
    }

    // Work with die centers on an index grid around the wafer center.
    const activeRadius = activeWafer / 2;
    const halfW = dieWidthVal / 2;
    const halfH = dieHeightVal / 2;

    // Conservative search bounds: number of cells that fit across the active wafer plus a small margin
    const cols = Math.ceil(activeWafer / dieWidthVal) + 2;
    const rows = Math.ceil(activeWafer / dieHeightVal) + 2;
    const iMin = -Math.floor(cols / 2);
    const iMax = iMin + cols - 1;
    const jMin = -Math.floor(rows / 2);
    const jMax = jMin + rows - 1;

    // Collect candidate dies with their grid indices and centered coordinates
    const candidates: Array<{ i: number; j: number; cx: number; cy: number }> = [];
    for (let j = jMin; j <= jMax; j++) {
      for (let i = iMin; i <= iMax; i++) {
        const cx = i * dieWidthVal + xOffset;
        const cy = j * dieHeightVal + yOffset;
        if (isRectangleInsideCircle(cx, cy, halfW, halfH, activeRadius)) {
          candidates.push({ i, j, cx, cy });
        }
      }
    }

    if (candidates.length === 0) return [];

    // Choose reference die: either from provided coordinates or the top-most-left die
    let refCandidate = candidates[0];

    if (
      referenceDieX !== null &&
      referenceDieX !== undefined &&
      referenceDieY !== null &&
      referenceDieY !== undefined
    ) {
      // find nearest candidate to provided reference coords (in µm)
      let bestDist = Infinity;
      for (const c of candidates) {
        const dx = c.cx - referenceDieX;
        const dy = c.cy - referenceDieY;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestDist) {
          bestDist = d2;
          refCandidate = c;
        }
      }
    } else {
      // top-most (smallest cy in centered coords -> visual top) then left-most (smallest cx)
      let yTop = Infinity;
      for (const c of candidates) {
        if (c.cy < yTop) yTop = c.cy;
      }
      const topRow = candidates.filter((c) => c.cy === yTop);
      refCandidate = topRow.reduce((best, cur) => (cur.cx < best.cx ? cur : best), topRow[0]);
    }

    const refI = refCandidate.i;
    const refJ = refCandidate.j;
    const minSize = Math.min(dieWidthVal, dieHeightVal);
    const streetSize =
      clampedStreet === 0 ? 0 : ((minSize - 2000) / minSize) * clampedStreet + 2000;

    // Build final positions using coordinates relative to reference die
    const positions: WaferDie[] = [];
    for (const c of candidates) {
      const col = c.i - refI;
      const row = c.j - refJ;
      const centerX = radius + c.cx;
      const centerY = radius + c.cy;
      positions.push({
        id: `${col}:${row}`,
        columnIndex: col,
        rowIndex: row,
        x: centerX - halfW,
        y: centerY - halfH,
        centerX,
        centerY,
        width: dieWidthVal - streetSize,
        height: dieHeightVal - streetSize,
        isReference: col === 0 && row === 0,
      });
    }

    // Sort so top rows come first and left-to-right within a row (stable visual order)
    // Ensure reference die is rendered last (on top) to avoid UI glitches
    positions.sort((a, b) => {
      if (a.isReference && !b.isReference) return 1;
      if (b.isReference && !a.isReference) return -1;
      if (a.centerY !== b.centerY) return b.centerY - a.centerY;
      return a.centerX - b.centerX;
    });

    return positions;
  }, [
    clampedWaferDiameter,
    clampedDieWidth,
    clampedDieHeight,
    clampedEdgeIndent,
    clampedDieOffsetX,
    clampedDieOffsetY,
    clampedStreet,
    radius,
    referenceDieX,
    referenceDieY,
  ]);

  // Notify parent when dies are calculated
  useEffect(() => {
    if (onDiesCalculated) {
      onDiesCalculated(dies);
    }
  }, [dies, onDiesCalculated]);

  const handleDieClick = (die: WaferDie) => {
    if (onDieClick) {
      onDieClick(die);
    }
  };

  // Create a map of die ID to selection order (1-based index)
  const selectionOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    selectedDieIds.forEach((id, index) => {
      map.set(id, index + 1);
    });
    return map;
  }, [selectedDieIds]);

  // Calculate font size based on die dimensions (20% of smaller dimension)
  const fontSize = useMemo(() => {
    const minDim = Math.min(clampedDieWidth, clampedDieHeight);
    return minDim * 0.4;
  }, [clampedDieWidth, clampedDieHeight]);

  return (
    <div className={cn("flex gap-4", className)}>
      <svg
        aria-label={ariaLabel}
        viewBox={viewBox}
        className="block w-full h-full flex-shrink-0"
        focusable="false"
        preserveAspectRatio="xMidYMid meet"
        role="img"
      >
        {/* Wafer circle */}
        <circle
          cx={radius}
          cy={radius}
          r={radius}
          className="fill-gray-200 stroke-gray-400 stroke-1"
          vectorEffect="non-scaling-stroke"
        />

        {/* Edge indent boundary */}
        {clampedEdgeIndent > 0 && edgeIndentRadius < radius && (
          <circle
            cx={radius}
            cy={radius}
            r={edgeIndentRadius}
            className="fill-none stroke-gray-500 stroke-1"
            strokeDasharray={`${clampedWaferDiameter * 0.05} ${clampedWaferDiameter * 0.025}`}
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* Dies */}
        {dies.map((die) => {
          const selectionOrder = selectionOrderMap.get(die.id);
          const isSelected = selectionOrder !== undefined;
          return (
            <g key={die.id}>
              <rect
                height={die.height}
                width={die.width}
                x={die.x}
                y={die.y}
                className={cn(
                  "cursor-pointer transition-colors",
                  die.isReference
                    ? isSelected
                      ? "fill-blue-500 stroke-blue-600 stroke-2 hover:fill-blue-800"
                      : "fill-blue-100 stroke-blue-600 stroke-2 hover:fill-blue-400"
                    : isSelected
                      ? "fill-gray-500 stroke-gray-950 stroke-1 hover:fill-gray-800"
                      : "fill-white stroke-gray-500 stroke-1 hover:fill-gray-800 hover:stroke-gray-950",
                )}
                vectorEffect="non-scaling-stroke"
                onClick={() => handleDieClick(die)}
              />
              {isSelected && (
                <text
                  x={die.centerX}
                  y={die.centerY}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={fontSize}
                  fontWeight="bold"
                  className="fill-white pointer-events-none select-none"
                  style={{ userSelect: "none" }}
                >
                  {selectionOrder}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
